import { useMemo, useState } from 'react';
import { Button, FeedbackBanner } from '@usacow/ds';
import { buildFeedbackEvent, type FeedbackEvent, type GradeResult } from '@usacow/engines';
import type { DrillKind } from './types.js';
import { pickDrill, type DrillContent } from './content.js';
import { gradeDrill, type DrillResponse } from './grade.js';
import { SelectRunner, TagRunner, PredictRunner, FillRunner, emptyResponse } from './runners.js';

const fbState = (g: GradeResult) =>
  g.status === 'correct' ? 'correct' : g.status === 'partially_correct' ? 'partial' : 'incorrect';

function RunnerFor({
  content,
  value,
  onChange,
  disabled,
}: {
  content: DrillContent;
  value: DrillResponse;
  onChange: (v: DrillResponse) => void;
  disabled: boolean;
}) {
  switch (content.archetype) {
    case 'select':
      return (
        <SelectRunner
          content={content}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'tag':
      return (
        <TagRunner
          content={content}
          value={value as Record<string, string>}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'predict':
      return (
        <PredictRunner
          content={content}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'fill':
      return (
        <FillRunner
          content={content}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
        />
      );
  }
}

export interface DrillHostProps {
  kind: DrillKind;
  /** Called after the student sees feedback and continues. */
  onComplete?: (result: {
    content: DrillContent;
    response: DrillResponse;
    grade: GradeResult;
    event: FeedbackEvent;
  }) => void;
  /** Lets the host (workout/guided-step) override the continue label. */
  continueLabel?: string;
}

/** Renders the right runner for a drill kind, grades on submit, shows feedback. */
export function DrillHost({ kind, onComplete, continueLabel = 'Continue' }: DrillHostProps) {
  const content = useMemo(() => pickDrill(kind), [kind]);
  const [value, setValue] = useState<DrillResponse>(() => (content ? emptyResponse(content) : ''));
  const [graded, setGraded] = useState<{ grade: GradeResult; event: FeedbackEvent } | null>(null);

  if (!content) return <div className="dr-empty">No drill content for “{kind}” yet.</div>;

  const empty =
    content.archetype === 'tag' ? Object.keys(value).length === 0 : (value as string).trim() === '';

  function submit() {
    const grade = gradeDrill(content!, value);
    const event = buildFeedbackEvent({ responseId: content!.id, grade });
    setGraded({ grade, event });
  }

  return (
    <div className="dr">
      <div className="dr-head">
        <div className="t-eyebrow">
          {kind.replace(/_/g, ' ')} · {content.topic.replace(/_/g, ' ')}
        </div>
      </div>
      <p className="dr-q">{content.prompt}</p>

      <RunnerFor content={content} value={value} onChange={setValue} disabled={graded != null} />

      {!graded ? (
        <div className="dr-foot">
          <Button variant="primary" disabled={empty} onClick={submit}>
            Check
          </Button>
        </div>
      ) : (
        <>
          <FeedbackBanner
            state={fbState(graded.grade)}
            title={
              graded.grade.status === 'correct'
                ? 'Correct.'
                : graded.grade.status === 'partially_correct'
                  ? 'Close.'
                  : 'Not quite.'
            }
            slug={graded.event.repair_action ? `repair: ${graded.event.repair_action}` : undefined}
            style={{ marginTop: 14 }}
          >
            {content.explanation}
          </FeedbackBanner>
          <div className="dr-foot">
            <Button
              variant="primary"
              onClick={() => onComplete?.({ content, response: value, ...graded })}
            >
              {continueLabel}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
